import {
  DecisionPageLayout,
  DecisionSection,
  DecisionSubHeading,
  DecisionList,
  InlineRelatedLinks,
} from "@/components/DecisionPageLayout";
import { Link } from "@/lib/router-compat";
import { allDecisionLinks } from "@/components/RelatedPages";
const seo = {
  title:
    "Federal Employee Mental Health & TMS Benefits | Neuro Wellness TMS Centers of America",
  description:
    "Federal employees, annuitants, and postal workers: learn about TMS, SAINT TMS, and depression treatment options through FEHB and PSHB plans. Verify your benefits today.",
  canonical: "/federal-employee-mental-health",
  keywords:
    "federal employee TMS, FEHB TMS coverage, federal employee SAINT TMS, federal employee depression treatment, PSHB mental health benefits",
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    name: "Federal Employee Mental Health & TMS Benefits",
    description:
      "Mental health treatment options for federal employees, annuitants, and postal workers through FEHB and PSHB plans.",
    provider: {
      "@id": "https://neurowellnesstms.com/#organization",
    },
  },
};
const federalLinks = [
  {
    title: "Does Federal BCBS Cover SAINT?",
    href: "/does-federal-bcbs-cover-saint",
  },
  {
    title: "Federal Employee TMS",
    href: "/federal-employee-tms",
  },
  {
    title: "FEHB TMS Coverage",
    href: "/fehb-tms-coverage",
  },
  {
    title: "Federal Employee SAINT TMS",
    href: "/federal-employee-saint-tms",
  },
  {
    title: "Federal Employee Insurance Verification",
    href: "/federal-employee-insurance-verification",
  },
  {
    title: "Federal Employee Depression Treatment",
    href: "/federal-employee-depression-treatment",
  },
];
export default function FederalEmployeeHub() {
  return (
    <DecisionPageLayout
      seo={seo}
      badge="Federal Employee Resources"
      title="Federal Employee Mental Health & TMS Benefits"
      subtitle="If you're a federal employee, annuitant, postal worker, or family member, this page explains what treatment options may be available through your health plan and how to find out."
    >
      <DecisionSection heading="Who this page is for">
        <p>
          You may be the person holding the insurance policy — a federal
          employee or annuitant researching on behalf of yourself, a spouse, or
          an adult child under 26. Or you may be the person who is struggling
          and reading this at a difficult hour.
        </p>
        <p>
          Either way, the information below is written for you. It covers what
          the Federal Employees Health Benefits (FEHB) program may cover, how
          SAINT and TMS fit in, and what to do next.
        </p>
      </DecisionSection>

      <DecisionSection heading="The basics: FEHB and PSHB">
        <p>
          Federal employees and annuitants receive health insurance through the
          Federal Employees Health Benefits (FEHB) program. Postal employees and
          annuitants, as of 2025, are covered under the separate Postal Service
          Health Benefits (PSHB) Program.
        </p>
        <p>
          FEHB plans are offered by carriers like Blue Cross Blue Shield (FEP
          Blue), Aetna, GEHA, MHBP, and others. Each plan has its own benefits,
          network, and coverage rules. What is covered under one plan may differ
          under another — even within the same carrier.
        </p>
      </DecisionSection>

      <DecisionSection heading="Does FEHB cover TMS?">
        <p>
          TMS may be covered when medical necessity criteria are met. The
          specific requirements depend on the carrier and plan you're enrolled
          in. FEP medical policy, for example, names the SAINT protocol as an
          example of accelerated theta burst stimulation — which is unusual
          compared with many commercial policies.
        </p>
        <p>
          However, policy language is not the same as a payment guarantee.
          Coverage depends on your specific plan, your diagnosis, your treatment
          history, and whether prior authorization is obtained.
        </p>
        <DecisionSubHeading>What we do</DecisionSubHeading>
        <p>
          Our team verifies benefits before any financial commitment. We gather
          records, submit prior authorization requests, and handle appeals if
          needed. You should not have to fight an insurer alone.
        </p>
      </DecisionSection>

      <DecisionSection heading="Who is covered on a federal plan">
        <DecisionList
          items={[
            "Spouse, including a valid common-law marriage",
            "Children under 26 — biological, adopted, step, foster, and recognized natural children",
            "Married children under 26 are eligible, but their spouses and children are not",
            "Disabled adult children 26 or older, where the disability existed before age 26",
          ]}
        />
        <p className="text-sm text-muted-foreground pt-2">
          Not eligible: former spouses (even under court order), grandchildren
          (unless they qualify as foster children), parents, and domestic
          partners.
        </p>
      </DecisionSection>

      <DecisionSection heading="Open Season">
        <p>
          The 2027 Open Season runs November 9 through December 14, 2026. If
          you're adding a family member to your plan, your employer will need to
          verify their eligibility — this is a new requirement for 2027.
        </p>
      </DecisionSection>

      <DecisionSection heading="Where to go next">
        <p>
          We've created specific pages for federal employees covering TMS,
          SAINT, FEHB coverage, insurance verification, and depression
          treatment. Start with the one that matches your question.
        </p>
      </DecisionSection>

      <InlineRelatedLinks
        heading="Federal Employee Resources"
        links={federalLinks}
      />
      <InlineRelatedLinks
        heading="Decision & Insurance Resources"
        links={allDecisionLinks.slice(0, 4)}
      />
    </DecisionPageLayout>
  );
}
